/**
 * Helper commun pour formatter proprement les erreurs API en français
 * et éviter l'affichage brut `[object Object]` dans les toasts.
 *
 * Accepte :
 *  - un Error (Error.message)
 *  - un objet {detail: "..."} (FastAPI 4xx)
 *  - un objet {detail: [{msg, loc, ...}]} (FastAPI 422 pydantic)
 *  - une string directe
 *  - null / undefined
 */

export function formatApiError(err, fallback = 'Une erreur est survenue') {
  if (!err) return fallback;

  // Error instance (fetch wrapped)
  if (err instanceof Error) {
    // Si le message est un JSON stringifié d'objet Pydantic, essaye de le parser
    try {
      const parsed = JSON.parse(err.message);
      return formatApiError(parsed, fallback);
    } catch { /* pas un JSON, on continue */ }
    return err.message && err.message !== '[object Object]' ? err.message : fallback;
  }

  // String brute
  if (typeof err === 'string') return err;

  // Objet FastAPI {detail: ...}
  if (typeof err === 'object') {
    const detail = err.detail ?? err.message ?? err.error;
    if (!detail) return fallback;

    // FastAPI 422 → array d'objets {msg, loc, type}
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          if (typeof d === 'string') return d;
          const loc = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : '';
          const msg = d.msg || d.message || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean)
        .slice(0, 3)
        .join(' · ');
    }

    // FastAPI 4xx → string
    if (typeof detail === 'string') return detail;

    // Objet arbitraire
    return JSON.stringify(detail);
  }

  return fallback;
}

/**
 * Parse la réponse d'un fetch et lève une Error avec message humain si !ok.
 * Usage :
 *   const res = await fetch(...);
 *   await throwIfNotOk(res);
 *   const data = await res.json();
 */
export async function throwIfNotOk(res, fallback = 'Erreur serveur') {
  if (res.ok) return;
  let body = null;
  try { body = await res.json(); } catch { /* pas de JSON */ }
  throw new Error(formatApiError(body, `${fallback} (${res.status})`));
}
